import { useState } from 'react'
import { useApolloClient, type ApolloClient, type ApolloQueryResult } from '@apollo/client'
import { uniqBy, zip } from 'lodash'
import { errors } from '@retailer-platform/dashboard/errors'
import { type LazyResponse } from '../../../api'
import {
  type GetDashboardUserSearchResultListQuery,
  type GetDashboardUserSearchResultListQueryVariables,
  GetDashboardUserSearchResultListDocument,
  type Instacart_Types_V1_RequestContext_Input,
  type InstacartCustomersSharedV1RequestContext,
} from '../../../../api'
import { type DashboardUserSearchResult } from './types'
// Removed unused eager/paginated hooks; OOD uses the fan-out wrapper below.

// --- Fan-out wrapper that zips requestContext to each user (moved from OOD) ---

export type DashboardUserWithRequestContext = DashboardUserSearchResult & {
  requestContext: Required<Omit<InstacartCustomersSharedV1RequestContext, '__typename'>>
}

export type UseGetDashboardUsersVariables = {
  requestContexts: InstacartCustomersSharedV1RequestContext[]
  itemsPerPage?: number
} & {
  emailSearchScenario?: GetDashboardUserSearchResultListQueryVariables['emailSearchScenario']
  phoneSearchScenario?: GetDashboardUserSearchResultListQueryVariables['phoneSearchScenario']
  loyaltySearchScenario?: GetDashboardUserSearchResultListQueryVariables['loyaltySearchScenario']
}

const toFrigateRequestContext = (
  ctx: InstacartCustomersSharedV1RequestContext
): Instacart_Types_V1_RequestContext_Input => ({
  clientConfigurationId: ctx.clientConfigurationId ?? undefined,
  storeConfigurationId: ctx.storeConfigurationId ?? undefined,
  countryId: ctx.countryId ?? undefined,
})

const listDashboardUsersByPersonalIdentifiers = ({
  client,
  requestContext,
  variables,
  itemsPerPage,
}: {
  client: ApolloClient<unknown>
  requestContext: InstacartCustomersSharedV1RequestContext
  variables: GetDashboardUserSearchResultListQueryVariables
  itemsPerPage: number
}) =>
  client.query<
    GetDashboardUserSearchResultListQuery,
    GetDashboardUserSearchResultListQueryVariables
  >({
    query: GetDashboardUserSearchResultListDocument,
    variables: {
      requestContext: toFrigateRequestContext(requestContext),
      paginationInfo: { limit: itemsPerPage },
      emailSearchScenario: variables.emailSearchScenario,
      phoneSearchScenario: variables.phoneSearchScenario,
      loyaltySearchScenario: variables.loyaltySearchScenario,
    },
    fetchPolicy: 'network-only',
  })

const mapFanoutResponse = (
  responses: ApolloQueryResult<GetDashboardUserSearchResultListQuery>[],
  variables: UseGetDashboardUsersVariables
): DashboardUserWithRequestContext[] => {
  const { requestContexts } = variables
  const zipped = zip(responses, requestContexts)
  const annotatedUsers = zipped.flatMap(([result, requestContext]) => {
    const node =
      result?.data
        ?.instacart_enterprise_frigate_fulfillment_v1_DashboardUserService_GetDashboardUserSearchResultList
    const users = (node?.results ?? []) as DashboardUserSearchResult[]
    return users.map(user => ({
      ...user,
      requestContext: {
        clientConfigurationId: requestContext?.clientConfigurationId as number,
        storeConfigurationId: requestContext?.storeConfigurationId as number,
        countryId: requestContext?.countryId as number,
      },
    }))
  })
  return uniqBy(annotatedUsers, user => user.id)
}

export const useGetDashboardUsersByPersonalIdentifiers = (): LazyResponse<
  DashboardUserWithRequestContext[],
  UseGetDashboardUsersVariables
> => {
  const client = useApolloClient()
  const [responses, setResponses] = useState<
    ApolloQueryResult<GetDashboardUserSearchResultListQuery>[] | null
  >(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [variables, setVariables] = useState<UseGetDashboardUsersVariables | null>(null)

  const execute = (options?: { variables?: UseGetDashboardUsersVariables }) => {
    if (!options?.variables) return
    const nextVariables = options.variables
    setVariables(nextVariables)

    async function asyncExecute() {
      try {
        setResponses(null)
        setIsLoading(true)

        const requestContexts = nextVariables.requestContexts ?? []
        const itemsPerPage = nextVariables.itemsPerPage ?? 20

        const { emailSearchScenario, phoneSearchScenario, loyaltySearchScenario } = nextVariables

        const baseVariables: GetDashboardUserSearchResultListQueryVariables = {
          emailSearchScenario,
          phoneSearchScenario,
          loyaltySearchScenario,
        }

        const queries = requestContexts.map(requestContext =>
          listDashboardUsersByPersonalIdentifiers({
            client,
            requestContext,
            variables: baseVariables,
            itemsPerPage,
          })
        )
        const results = await Promise.all(queries)
        setResponses(results)
      } catch (error: unknown) {
        errors.captureException(error as Error)
      } finally {
        setIsLoading(false)
      }
    }

    asyncExecute()
  }

  if (responses && variables) {
    return {
      type: 'success',
      isLoading: false,
      data: mapFanoutResponse(responses, variables),
      execute,
      networkStatus: 1,
    }
  }

  return {
    type: 'pending',
    isLoading,
    execute,
    networkStatus: 1,
  }
}
